import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import Loading from './Loading';

const ROLE_OPTIONS = [
  { value: 'engineer', label: 'Engineer' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'admin', label: 'Admin' },
  { value: 'director', label: 'Director' },
];

const formatRole = (role) => {
  const match = ROLE_OPTIONS.find((opt) => opt.value === role);
  return match ? match.label : (role || 'No role');
};

function AdminApproval() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingRoles, setPendingRoles] = useState({});
  const [approvedRoles, setApprovedRoles] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const { currentUser } = useAuth();

  const isAdmin = currentUser && currentUser.email === 'dan@flair.ac';

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const pending = [];
      const approved = [];

      snapshot.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() };
        if (data.approved === true) {
          approved.push(data);
        } else {
          pending.push(data);
        }
      });

      approved.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      pending.sort((a, b) => (a.email || '').localeCompare(b.email || ''));

      setPendingUsers(pending);
      setApprovedUsers(approved);

      const nextApprovedRoles = {};
      approved.forEach((user) => {
        nextApprovedRoles[user.id] = user.role || '';
      });
      setApprovedRoles(nextApprovedRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleApprove = async (userId) => {
    const role = pendingRoles[userId];
    if (!role) {
      alert('Please select a role before approving');
      return;
    }

    try {
      setActionLoading(`approve-${userId}`);
      await updateDoc(doc(db, 'users', userId), {
        approved: true,
        role,
        approvedAt: new Date().toISOString(),
      });

      setPendingUsers((prev) => prev.filter((user) => user.id !== userId));
      setPendingRoles((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      await fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Failed to approve user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (user) => {
    const confirmed = window.confirm(
      `Reject and delete the request for ${user.email}?\n\nThis removes their access record. They will need to sign up again if you want to approve them later.`
    );
    if (!confirmed) return;

    try {
      setActionLoading(`reject-${user.id}`);
      await deleteDoc(doc(db, 'users', user.id));
      setPendingUsers((prev) => prev.filter((u) => u.id !== user.id));
      setPendingRoles((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Failed to reject user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleUpdate = async (userId) => {
    const role = approvedRoles[userId];
    if (!role) {
      alert('Please select a role');
      return;
    }

    try {
      setActionLoading(`role-${userId}`);
      await updateDoc(doc(db, 'users', userId), {
        role,
        updatedAt: new Date().toISOString(),
      });
      setApprovedUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, role } : user))
      );
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        You are not authorized to view this page.
      </div>
    );
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <Link to="/dashboard" style={styles.homeButton}>
            ← Home
          </Link>
          <h2 style={styles.title}>Admin Approval</h2>
        </div>
      </div>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Pending Requests</h3>
        {pendingUsers.length === 0 ? (
          <p style={styles.empty}>No users pending approval.</p>
        ) : (
          <div style={styles.list}>
            {pendingUsers.map((user) => (
              <div key={user.id} style={styles.card}>
                <div style={styles.cardInfo}>
                  <div style={styles.email}>{user.email}</div>
                  {user.createdAt && (
                    <div style={styles.meta}>
                      Requested: {new Date(user.createdAt).toLocaleString()}
                    </div>
                  )}
                </div>
                <div style={styles.cardActions}>
                  <select
                    value={pendingRoles[user.id] || ''}
                    onChange={(e) =>
                      setPendingRoles((prev) => ({ ...prev, [user.id]: e.target.value }))
                    }
                    style={styles.select}
                  >
                    <option value="">Select Role</option>
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleApprove(user.id)}
                    disabled={!pendingRoles[user.id] || actionLoading === `approve-${user.id}`}
                    style={{
                      ...styles.approveButton,
                      opacity: !pendingRoles[user.id] || actionLoading === `approve-${user.id}` ? 0.6 : 1,
                    }}
                  >
                    {actionLoading === `approve-${user.id}` ? 'Approving…' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(user)}
                    disabled={actionLoading === `reject-${user.id}`}
                    style={styles.rejectButton}
                  >
                    {actionLoading === `reject-${user.id}` ? 'Deleting…' : 'Reject & Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Approved Users</h3>
        {approvedUsers.length === 0 ? (
          <p style={styles.empty}>No approved users yet.</p>
        ) : (
          <div style={styles.list}>
            {approvedUsers.map((user) => {
              const isMaster = user.email === 'dan@flair.ac';
              const roleChanged = (approvedRoles[user.id] || '') !== (user.role || '');

              return (
                <div key={user.id} style={styles.card}>
                  <div style={styles.cardInfo}>
                    <div style={styles.email}>
                      {user.email}
                      {isMaster ? <span style={styles.badge}>Master</span> : null}
                    </div>
                    <div style={styles.meta}>
                      Current role: {formatRole(user.role)}
                      {user.approvedAt
                        ? ` · Approved: ${new Date(user.approvedAt).toLocaleDateString()}`
                        : ''}
                    </div>
                  </div>
                  <div style={styles.cardActions}>
                    <select
                      value={approvedRoles[user.id] || ''}
                      onChange={(e) =>
                        setApprovedRoles((prev) => ({ ...prev, [user.id]: e.target.value }))
                      }
                      disabled={isMaster}
                      style={styles.select}
                    >
                      <option value="">Select Role</option>
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleRoleUpdate(user.id)}
                      disabled={isMaster || !roleChanged || actionLoading === `role-${user.id}`}
                      style={{
                        ...styles.updateButton,
                        opacity: isMaster || !roleChanged || actionLoading === `role-${user.id}` ? 0.6 : 1,
                      }}
                    >
                      {actionLoading === `role-${user.id}` ? 'Saving…' : 'Update Role'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 800,
    margin: '40px auto',
    padding: '0 16px 40px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 15,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 15,
  },
  homeButton: {
    padding: '8px 16px',
    backgroundColor: '#f0f2f5',
    color: '#2c3e50',
    textDecoration: 'none',
    borderRadius: 6,
    border: '1px solid #e1e5ea',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  title: {
    color: '#2AA4F7',
    margin: 0,
  },
  section: {
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: 24,
    marginBottom: 20,
  },
  sectionTitle: {
    margin: '0 0 16px',
    color: '#2c3e50',
    fontSize: '1.1rem',
  },
  empty: {
    margin: 0,
    color: '#64748b',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  cardInfo: {
    minWidth: 220,
    flex: 1,
  },
  email: {
    fontWeight: 600,
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  badge: {
    fontSize: '0.75rem',
    background: '#e8f5fe',
    color: '#2AA4F7',
    padding: '2px 8px',
    borderRadius: 999,
    fontWeight: 600,
  },
  meta: {
    marginTop: 4,
    fontSize: '0.85rem',
    color: '#64748b',
  },
  cardActions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  select: {
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid #d7dde5',
    minWidth: 160,
  },
  approveButton: {
    padding: '8px 14px',
    borderRadius: 6,
    border: 'none',
    background: '#27ae60',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
  },
  rejectButton: {
    padding: '8px 14px',
    borderRadius: 6,
    border: '1px solid #f5c6cb',
    background: '#ffecec',
    color: '#dc3545',
    fontWeight: 600,
    cursor: 'pointer',
  },
  updateButton: {
    padding: '8px 14px',
    borderRadius: 6,
    border: 'none',
    background: '#2AA4F7',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default AdminApproval;
